import styled, { css } from "styled-components";
import { device } from "@src/theme";

export const PlaylistOverviewHeader = styled.div`
  display: flex;
  height: 130px;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media ${device.mobile} {
    flex-direction: column;
    height: auto;
    align-items: flex-start;
    gap: ${({ theme }) => theme.margins.medium};
    padding: ${({ theme }) => theme.margins.small} 0;
  }
`;

export const PlaylistOverviewHeaderLeft = styled.div`
  display: flex;
  flex: 1;
  height: 100%;

  @media ${device.mobile} {
    width: 100%;
  }
`;

export const PlaylistOverviewHeaderRight = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  @media ${device.mobile} {
    width: 100%;
    align-items: flex-start;
  }
`;

export const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: 100%;
  gap: ${({ theme }) => theme.margins.small};
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray150};
  margin: 0;
  line-height: 1.4;
`;

export const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 280px;
  position: relative;

  @media ${device.mobile} {
    width: 100%;
  }
`;

export const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.margins.xl};
  box-sizing: border-box;

  @media ${device.mobile} {
    padding: ${({ theme }) => theme.margins.medium};
  }
`;

export const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.margins.large};
`;

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.margins.medium};
  color: ${({ theme }) => theme.colors.white};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  padding-left: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const PlaylistHeader = styled.div`
  margin-top: ${({ theme }) => theme.margins.large};
  margin-bottom: ${({ theme }) => theme.margins.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray400};
  padding-bottom: ${({ theme }) => theme.margins.small};
`;

export const PlaylistTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.margins.large};

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.margins.medium};
  }
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.gray300};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow:
      0 10px 20px rgba(255, 240, 87, 0.08),
      0 6px 6px rgba(0, 0, 0, 0.2);
  }
`;

export const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio */
  background: ${({ theme }) => theme.colors.darkerBg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray400};
  overflow: hidden;
`;

export const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

export const CardContent = styled.div`
  padding: ${({ theme }) => theme.margins.medium};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.small};
  flex-grow: 1;
`;

export const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.white};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
  padding-top: 6px;
`;

export const MetaBadge = styled.span`
  background-color: ${({ theme }) => theme.colors.gray400};
  color: ${({ theme }) => theme.colors.gray150};
  font-size: 10px;
  padding: 3px 6px;
  border-radius: 3px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

export const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.small};
  margin-top: ${({ theme }) => theme.margins.small};
`;

const promoActionButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 13px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray400};
    color: ${({ theme }) => theme.colors.gray200};
    cursor: not-allowed;
    transform: none;
  }
`;

export const StoryButton = styled.button`
  ${promoActionButtonStyles}
  background-color: ${({ theme }) => theme.colors.gray400};
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray200};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.01);
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }
`;

export const DownloadButton = styled.button`
  ${promoActionButtonStyles}
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.black};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.white};
    transform: scale(1.01);
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${({ theme }) => theme.margins.medium};
  color: ${({ theme }) => theme.colors.gray150};
  width: 100%;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  background: ${({ theme }) => theme.colors.gray300};
  border-radius: 12px;
  border: 1px dashed ${({ theme }) => theme.colors.gray400};
  color: ${({ theme }) => theme.colors.gray150};
  gap: ${({ theme }) => theme.margins.medium};
  width: 100%;
`;

export const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;
`;
